/**
 * @file Device input, normalised into abstract actions (GDD §21).
 *
 * Systems never read `KeyW` or `button 0`: they ask for `Action.MOVE_UP` or
 * `Action.FIRE`. Rebinding, and adding a gamepad, is therefore a change to the
 * binding tables only. Edge state (`justPressed`) is cleared by `endFrame()`,
 * which the engine calls after the world update.
 */

/**
 * Abstract inputs the game reacts to.
 * @readonly
 * @enum {string}
 */
export const Action = Object.freeze({
  MOVE_UP: 'moveUp',
  MOVE_DOWN: 'moveDown',
  MOVE_LEFT: 'moveLeft',
  MOVE_RIGHT: 'moveRight',
  FIRE: 'fire',
  /** Brief burst that stuns and disarms the first enemy hit. */
  DASH: 'dash',
  /** Hurls the equipped weapon away as a projectile. */
  THROW: 'throw',
  INTERACT: 'interact',
  RELOAD: 'reload',
  PAUSE: 'pause',
  CONFIRM: 'confirm',
  RESTART: 'restart',
  /**
   * Opens and closes the whole developer toolbox: AI overlay, wave sandbox and
   * spawn sandbox all live behind this one key (issue #56).
   */
  TOGGLE_DEBUG: 'toggleDebug',
  DEBUG_DUNGEON: 'debugDungeon'
});

/**
 * @type {Readonly<Record<string, string | string[]>>} `KeyboardEvent.code` ->
 * action, or several actions when one key means different things in different
 * states: R reloads during a run and restarts on the game-over screen, and only
 * one of those two ever has a listener.
 */
const KEYBOARD_BINDINGS = Object.freeze({
  KeyW: Action.MOVE_UP,
  ArrowUp: Action.MOVE_UP,
  KeyS: Action.MOVE_DOWN,
  ArrowDown: Action.MOVE_DOWN,
  KeyA: Action.MOVE_LEFT,
  ArrowLeft: Action.MOVE_LEFT,
  KeyD: Action.MOVE_RIGHT,
  ArrowRight: Action.MOVE_RIGHT,
  Space: Action.FIRE,
  KeyT: Action.THROW,
  KeyE: Action.INTERACT,
  Escape: Action.PAUSE,
  Enter: Action.CONFIRM,
  KeyR: [Action.RELOAD, Action.RESTART],
  // The whole developer toolbox lives on comma: a physical key every layout
  // has (PR #64 review: `*` must stay unbound).
  Comma: Action.TOGGLE_DEBUG,
  Minus: Action.DEBUG_DUNGEON
});

/**
 * Fallback table on lowercase `KeyboardEvent.key`.
 *
 * `code` is the correct thing to bind to — it is layout-independent — but it is
 * absent on some synthetic and virtual-keyboard events, and empty `code` would
 * silently swallow every keystroke. This table is the safety net.
 *
 * Debug mode binds `Comma` above and `,` here, so the fallback path keeps the
 * same key on synthetic events.
 * @type {Readonly<Record<string, string | string[]>>}
 */
const KEY_FALLBACK_BINDINGS = Object.freeze({
  w: Action.MOVE_UP,
  arrowup: Action.MOVE_UP,
  s: Action.MOVE_DOWN,
  arrowdown: Action.MOVE_DOWN,
  a: Action.MOVE_LEFT,
  arrowleft: Action.MOVE_LEFT,
  d: Action.MOVE_RIGHT,
  arrowright: Action.MOVE_RIGHT,
  ' ': Action.FIRE,
  t: Action.THROW,
  e: Action.INTERACT,
  escape: Action.PAUSE,
  enter: Action.CONFIRM,
  r: [Action.RELOAD, Action.RESTART],
  ',': Action.TOGGLE_DEBUG,
  '-': Action.DEBUG_DUNGEON
});

/** Printable names for `MouseEvent.button` values used in {@link MOUSE_BINDINGS}. */
const MOUSE_LABELS = Object.freeze({
  0: 'LMB',
  2: 'RMB'
});

/**
 * Printable labels per action, for anything that has to *show* a binding —
 * the menu legend, a debug window header. Derived from the binding tables so a
 * rebind cannot leave the on-screen legend lying.
 *
 * @param {string} action One of {@link Action}.
 * @returns {string[]} Every key that triggers it, prettified, without dupes.
 */
export function keyLabelsFor(action) {
  /** @type {Set<string>} */
  const labels = new Set();
  const matches = (binding) => (Array.isArray(binding) ? binding.includes(action) : binding === action);

  for (const [code, binding] of Object.entries(KEYBOARD_BINDINGS)) {
    if (matches(binding)) labels.add(prettifyCode(code));
  }
  for (const [key, binding] of Object.entries(KEY_FALLBACK_BINDINGS)) {
    if (matches(binding)) labels.add(key.length === 1 ? key.toUpperCase() : capitalise(key));
  }
  // Mouse buttons (dash on RMB, fire on LMB) are not KeyboardEvent codes, so
  // they have to be walked separately or the controls panel would never list them.
  for (const [button, binding] of Object.entries(MOUSE_BINDINGS)) {
    if (matches(binding)) labels.add(MOUSE_LABELS[button] ?? `Mouse${button}`);
  }
  return [...labels];
}

/**
 * @param {string} code A `KeyboardEvent.code`.
 * @returns {string} `KeyW` -> `W`, `NumpadMultiply` -> `Numpad *`.
 */
function prettifyCode(code) {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code === 'NumpadMultiply') return 'Numpad *';
  return code;
}

/**
 * @param {string} word Lowercase word.
 * @returns {string} Same word, first letter uppercased.
 */
function capitalise(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** @type {Readonly<Record<number, string>>} `MouseEvent.button` -> action. */
const MOUSE_BINDINGS = Object.freeze({
  0: Action.FIRE,
  2: Action.DASH
});

/** @type {Readonly<Record<number, string>>} Standard gamepad button -> action. */
const GAMEPAD_BINDINGS = Object.freeze({
  0: Action.CONFIRM,
  2: Action.RELOAD,
  4: Action.DASH,
  7: Action.FIRE,
  9: Action.PAUSE
});

/** Dead zone applied to gamepad sticks. */
const STICK_DEADZONE = 0.18;

/** Input types that swallow characters rather than trigger a control. */
const TEXT_INPUT_TYPES = new Set(['text', 'number', 'search', 'email', 'password', 'tel', 'url']);

/**
 * True when the keystroke is someone typing into a field — a debug window's
 * number box, say. Sliders and buttons are deliberately excluded: they take
 * arrow keys and Enter without stealing them from the game.
 *
 * @param {EventTarget | null} target Event target.
 * @returns {boolean} True when the key belongs to the field, not the game.
 */
function isTextEntry(target) {
  const element = /** @type {HTMLElement | null} */ (target);
  if (!element || typeof element.tagName !== 'string') return false;
  if (element.isContentEditable) return true;
  if (element.tagName === 'TEXTAREA') return true;
  if (element.tagName !== 'INPUT') return false;
  return TEXT_INPUT_TYPES.has(/** @type {HTMLInputElement} */ (element).type);
}

export class InputManager {
  /**
   * @param {HTMLElement} target Element that receives pointer events.
   */
  constructor(target) {
    /** @type {HTMLElement} @private */
    this._target = target;

    /** @type {Set<string>} Actions held down this frame. @private */
    this._down = new Set();
    /** @type {Set<string>} Actions that went down since the last `endFrame`. @private */
    this._pressed = new Set();
    /** @type {Set<string>} Actions released since the last `endFrame`. @private */
    this._released = new Set();

    /**
     * Pointer state in CSS pixels and normalised device coordinates.
     * @type {{ x: number, y: number, ndcX: number, ndcY: number, inside: boolean }}
     */
    this.pointer = { x: 0, y: 0, ndcX: 0, ndcY: 0, inside: false };

    /** @type {number} Accumulated wheel notches this frame; positive = zoom out. */
    this.wheel = 0;

    /** @type {{ x: number, y: number }} Left stick, dead-zoned. */
    this.moveStick = { x: 0, y: 0 };
    /** @type {{ x: number, y: number }} Right stick, dead-zoned. */
    this.aimStick = { x: 0, y: 0 };
    /** @type {boolean} True while a gamepad reports input. */
    this.gamepadActive = false;
    /** @type {boolean} True while an on-screen stick is deflected. @see setTouchStick */
    this.touchActive = false;
    /**
     * Whether this run is driven by touch. Set once from the resolved input
     * mode; it only gates the two guards below, which have to know a finger
     * from a mouse.
     * @type {boolean}
     */
    this.touchMode = false;

    /** @type {Array<[EventTarget, string, EventListener, any]>} @private */
    this._listeners = [];
    this._bindDomEvents();
  }

  /** @returns {void} @private */
  _bindDomEvents() {
    const add = (element, type, handler, options) => {
      element.addEventListener(type, handler, options);
      this._listeners.push([element, type, handler, options]);
    };

    add(window, 'keydown', (event) => {
      if (isTextEntry(event.target)) return;
      const action = this._resolveKey(/** @type {KeyboardEvent} */ (event));
      if (!action) return;
      // Stop the browser from scrolling or activating focused UI.
      event.preventDefault();
      if (/** @type {KeyboardEvent} */ (event).repeat) return;
      this._press(action);
    });

    add(window, 'keyup', (event) => {
      // Not gated on `isTextEntry`: a key pressed before focus moved into a
      // field must still be released, or it stays stuck down.
      const action = this._resolveKey(/** @type {KeyboardEvent} */ (event));
      if (action) this._release(action);
    });

    add(this._target, 'pointerdown', (event) => {
      // A finger always reports button 0, so without this every tap on the
      // canvas would fire the weapon behind the on-screen controls.
      if (this.touchMode && /** @type {PointerEvent} */ (event).pointerType === 'touch') return;
      const action = MOUSE_BINDINGS[/** @type {PointerEvent} */ (event).button];
      this._updatePointer(/** @type {PointerEvent} */ (event));
      if (action) this._press(action);
    });

    add(window, 'pointerup', (event) => {
      const action = MOUSE_BINDINGS[/** @type {PointerEvent} */ (event).button];
      if (action) this._release(action);
    });

    add(window, 'pointermove', (event) => {
      // Touch drags are the on-screen sticks, not the aim cursor. Letting them
      // through would drag `pointer.ndc` (the crosshair would snap to the thumb)
      // and clear `gamepadActive`, killing stick aim on the very next frame.
      if (this.touchMode && /** @type {PointerEvent} */ (event).pointerType === 'touch') return;
      this._updatePointer(/** @type {PointerEvent} */ (event));
      this.gamepadActive = false;
    });

    add(this._target, 'wheel', (event) => {
      event.preventDefault();
      this.wheel += Math.sign(/** @type {WheelEvent} */ (event).deltaY);
    }, { passive: false });

    add(this._target, 'contextmenu', (event) => event.preventDefault());

    // Releasing everything on blur avoids "stuck key" bugs after alt-tabbing.
    add(window, 'blur', () => this.releaseAll());
  }

  /**
   * Maps a keyboard event to an action, preferring the physical key code.
   * @param {KeyboardEvent} event Source event.
   * @returns {string | string[] | undefined} Action name (or names), or
   *   undefined when unbound.
   * @private
   */
  _resolveKey(event) {
    return (
      KEYBOARD_BINDINGS[event.code] ??
      KEY_FALLBACK_BINDINGS[(event.key ?? '').toLowerCase()]
    );
  }

  /**
   * @param {PointerEvent} event Source event.
   * @returns {void}
   * @private
   */
  _updatePointer(event) {
    const rect = this._target.getBoundingClientRect();
    this.pointer.x = event.clientX;
    this.pointer.y = event.clientY;
    this.pointer.ndcX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.ndcY = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.pointer.inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
  }

  /** @param {string | string[]} action Action name, or several. @returns {void} @private */
  _press(action) {
    if (Array.isArray(action)) {
      for (const one of action) this._press(one);
      return;
    }
    if (this._down.has(action)) return;
    this._down.add(action);
    this._pressed.add(action);
  }

  /** @param {string | string[]} action Action name, or several. @returns {void} @private */
  _release(action) {
    if (Array.isArray(action)) {
      for (const one of action) this._release(one);
      return;
    }
    if (!this._down.delete(action)) return;
    this._released.add(action);
  }

  /**
   * Whether `moveStick`/`aimStick` are being driven at all.
   *
   * Gamepad and on-screen sticks are one flag downstream on purpose: both fill
   * the same two analog vectors, and no consumer should have to care which
   * device filled them in.
   *
   * @returns {boolean}
   */
  get analogActive() {
    return this.gamepadActive || this.touchActive;
  }

  /**
   * Presses an action from a source that is neither a key nor a mouse button —
   * an on-screen button, today. Idempotent: {@link InputManager#_press} ignores
   * an action already held, so a held button can call this every frame.
   *
   * @param {string} action One of {@link Action}.
   * @returns {void}
   */
  pressVirtual(action) {
    this._press(action);
  }

  /**
   * @param {string} action One of {@link Action}.
   * @returns {void}
   */
  releaseVirtual(action) {
    this._release(action);
  }

  /**
   * Writes an on-screen stick, in the same normalised space the gamepad uses.
   *
   * @param {'move' | 'aim'} which Which stick.
   * @param {number} x -1..1.
   * @param {number} y -1..1.
   * @returns {void}
   */
  setTouchStick(which, x, y) {
    const stick = which === 'aim' ? this.aimStick : this.moveStick;
    stick.x = x;
    stick.y = y;
    this.touchActive =
      this.moveStick.x !== 0 ||
      this.moveStick.y !== 0 ||
      this.aimStick.x !== 0 ||
      this.aimStick.y !== 0;
  }

  /**
   * Zeroes both on-screen sticks. `releaseAll()` drops held actions but cannot
   * know about analog state, so pausing has to come through here too or the
   * player keeps drifting after the menu closes.
   * @returns {void}
   */
  clearTouchSticks() {
    this.moveStick.x = 0;
    this.moveStick.y = 0;
    this.aimStick.x = 0;
    this.aimStick.y = 0;
    this.touchActive = false;
  }

  /**
   * @param {string} action Action name.
   * @returns {boolean} True while the action is held.
   */
  isDown(action) {
    return this._down.has(action);
  }

  /**
   * @param {string} action Action name.
   * @returns {boolean} True only on the frame the action went down.
   */
  justPressed(action) {
    return this._pressed.has(action);
  }

  /**
   * @param {string} action Action name.
   * @returns {boolean} True only on the frame the action went up.
   */
  justReleased(action) {
    return this._released.has(action);
  }

  /**
   * Movement intent on the XZ plane, already normalised to length <= 1.
   * Keyboard and left stick are merged, so both work at once.
   * @param {{ x: number, y: number }} out Vector reused to avoid allocation.
   * @returns {{ x: number, y: number }} The same `out` object.
   */
  getMoveVector(out) {
    let x = (this.isDown(Action.MOVE_RIGHT) ? 1 : 0) - (this.isDown(Action.MOVE_LEFT) ? 1 : 0);
    let y = (this.isDown(Action.MOVE_DOWN) ? 1 : 0) - (this.isDown(Action.MOVE_UP) ? 1 : 0);

    if (this.analogActive) {
      x += this.moveStick.x;
      y += this.moveStick.y;
    }

    const length = Math.hypot(x, y);
    if (length > 1) {
      x /= length;
      y /= length;
    }

    out.x = x;
    out.y = y;
    return out;
  }

  /**
   * Polls connected gamepads. Called by the engine before the world update.
   * @returns {void}
   */
  pollGamepad() {
    const pads = navigator.getGamepads?.() ?? [];
    const pad = Array.prototype.find.call(pads, (candidate) => candidate && candidate.connected);
    if (!pad) return;

    const deadzone = (value) => (Math.abs(value) < STICK_DEADZONE ? 0 : value);

    this.moveStick.x = deadzone(pad.axes[0] ?? 0);
    this.moveStick.y = deadzone(pad.axes[1] ?? 0);
    this.aimStick.x = deadzone(pad.axes[2] ?? 0);
    this.aimStick.y = deadzone(pad.axes[3] ?? 0);

    for (const [index, action] of Object.entries(GAMEPAD_BINDINGS)) {
      const button = pad.buttons[Number(index)];
      if (!button) continue;
      if (button.pressed) this._press(action);
      else this._release(action);
    }

    // Analog triggers double as fire on pads without a digital right trigger.
    if ((pad.buttons[7]?.value ?? 0) > 0.4) this._press(Action.FIRE);

    const active =
      this.moveStick.x !== 0 ||
      this.moveStick.y !== 0 ||
      this.aimStick.x !== 0 ||
      this.aimStick.y !== 0;
    if (active) this.gamepadActive = true;
  }

  /**
   * Clears per-frame edge state. Must run once per frame, after the update.
   * @returns {void}
   */
  endFrame() {
    this._pressed.clear();
    this._released.clear();
    this.wheel = 0;
  }

  /**
   * Drops every held action, as if the player let go of everything.
   * @returns {void}
   */
  releaseAll() {
    for (const action of this._down) this._released.add(action);
    this._down.clear();
  }

  /**
   * Removes every DOM listener.
   * @returns {void}
   */
  dispose() {
    for (const [element, type, handler, options] of this._listeners) {
      element.removeEventListener(type, handler, options);
    }
    this._listeners.length = 0;
  }
}
